import JSZip from 'jszip';

export interface PolarOvalPoint {
  lat: number;
  lon: number;
}

export interface PolarElectroJetPoint {
  lat: number;
  lon: number;
}

export interface OvationAuroraCoordinate {
  longitude: number;
  latitude: number;
  auroraValue: number;
}

interface ApiError {
  error: string;
  details?: string;
}

export interface Station {
  name: string;
  code: string;
  coordinates: {
    lat: number;
    long: number;
  };
  magdata: {
    h: number;
    d: number;
    z: number;
    timestamp: string | null;
  };
  quiet_level: {
    h: number;
    z: number;
  };
}

export class AuroraApi {

  private static readonly BASE_URL = import.meta.env.DEV ? '' : 'https://aurora.narwhal.tech';

  /**
   * Fetches the polar oval data from the aurora API
   * @returns Promise containing an array of lat/lon points defining the polar oval
   * @throws Error if the API request fails or returns invalid data
   */
  static async getPolarOval(): Promise<PolarOvalPoint[]> {

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.BASE_URL}/polar_oval.zip`, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json() as ApiError;
        throw new Error(errorData.details || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const zip = new JSZip();
      await zip.loadAsync(arrayBuffer);

      const jsonFile = zip.file('polar_oval.json');
      if (!jsonFile) {
        throw new Error('polar_oval.json not found in ZIP file');
      }

      const jsonData = await jsonFile.async('string');
      const data = JSON.parse(jsonData);

      // Validate the data structure
      if (!Array.isArray(data)) {
        throw new Error('Invalid response: expected an array of points');
      }

      // Validate each point
      data.forEach((point: any, index) => {
        if (typeof point.lat !== 'number' || typeof point.lon !== 'number') {
          throw new Error(`Invalid point at index ${index}: expected lat and lon to be numbers`);
        }
      });

      return data as PolarOvalPoint[];
    } catch (error) {
      console.error('Error fetching polar oval data:', error);
      return [];
    }
  }

  static async getStations(): Promise<Station[]> {

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${this.BASE_URL}/stations.json`, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json() as ApiError;
        throw new Error(errorData.details || errorData.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching stations:', error);
      return [];
    }
  }

  /**
   * Fetches the Polar Electro Jet data
   * @returns Promise containing an array of lat/lon points defining the polar electro jet
   * @throws Error if the API request fails or returns invalid data
   */
  static async getPolarElectroJet(): Promise<PolarElectroJetPoint[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Use localhost in development mode, production URL otherwise
      const url = import.meta.env.DEV
        ? `${this.BASE_URL}/test/aurora_high_1.json`
        : `${this.BASE_URL}/aurora.json`;

      const response = await fetch(url, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate the data structure
      if (!Array.isArray(data)) {
        throw new Error('Invalid response: expected an array of coordinates');
      }

      // Validate each point
      data.forEach((point: any, index) => {
        if (typeof point.lat !== 'number' || typeof point.lon !== 'number') {
          throw new Error(`Invalid point at index ${index}: expected lat and lon to be numbers`);
        }
      });

      return data as PolarElectroJetPoint[];
    } catch (error) {
      console.error('Error fetching Polar Electro Jet data:', error);
      return [];
    }
  }

  /**
   * Fetches the NOAA Ovation Aurora model data
   * @returns Promise containing an array of coordinates with aurora intensity values
   * @throws Error if the API request fails or returns invalid data
   */
  static async getOvationAurora(): Promise<OvationAuroraCoordinate[]> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch('https://services.swpc.noaa.gov/json/ovation_aurora_latest.json', {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate the data structure
      if (!data.coordinates || !Array.isArray(data.coordinates)) {
        throw new Error('Invalid response: expected coordinates array');
      }

      // Transform the data from [longitude, latitude, auroraValue] to our interface
      // Filter out low values (< 6) to reduce data size
      const filtered = data.coordinates
        .filter((c: [number, number, number]) => c[2] >= 3 && (c[1] >= 10 || c[1] <= -10))
        .map((coord: [number, number, number]) => ({
          longitude: coord[0],
          latitude: coord[1],
          auroraValue: coord[2]
        }));

      return filtered;
    } catch (error) {
      console.error('Error fetching Ovation Aurora data:', error);
      return [];
    }
  }
}